import {ProcedureRunDataEntryMode} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ExperimentalInputs} from 'procedures/planning/ProcedurePlanTypes';
import {ConditionGroup} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';

export enum ProcedureRunTab {
  OVERVIEW = 'overview',
  DESIGN = 'design',
}

export interface ProcedureRunOrPlanStateSummary {
  id: string | null;
  name: string | null;
  procedureId?: string | null;
}

export interface ProcedureRunPlanDesignStateSummary {
  conditions: Array<ConditionGroup>;
  experimentalInputs: ExperimentalInputs;
  dataEntryMode: ProcedureRunDataEntryMode;
  isInEditMode: boolean;
  // Whether the user has made unsaved edits since the form was last clean (initial load or last
  // successful save). Persisted so the Save button's enabled state survives navigating away from and
  // back to the plan, and so a re-mount can rehydrate the dirty state instead of resetting it to clean.
  isDirty: boolean;
}

export enum ProcedureRunPlanUIActionType {
  SET_SELECTED_PROCEDURE_RUN = 'PROCEDURE_RUN_PLAN_UI_SET_SELECTED_PROCEDURE_RUN',
  SET_SELECTED_PROCEDURE_RUN_PLAN = 'PROCEDURE_RUN_PLAN_UI_SET_SELECTED_PROCEDURE_RUN_PLAN',
  SET_CURRENT_PROCEDURE_RUN_TAB = 'PROCEDURE_RUN_PLAN_UI_SET_CURRENT_PROCEDURE_RUN_TAB',
  ADD_PROMOTING_PLAN_ID = 'PROCEDURE_RUN_PLAN_UI_ADD_PROMOTING_PLAN_ID',
  REMOVE_PROMOTING_PLAN_ID = 'PROCEDURE_RUN_PLAN_UI_REMOVE_PROMOTING_PLAN_ID',
  SET_PROCEDURE_RUN_PLAN_DESIGN_STATE = 'PROCEDURE_RUN_PLAN_UI_SET_PROCEDURE_RUN_PLAN_DESIGN_STATE',
  SET_IS_IN_EDIT_MODE = 'PROCEDURE_RUN_PLAN_UI_SET_IS_IN_EDIT_MODE',
  CLEAR_PROCEDURE_RUN_PLAN_SELECTION = 'PROCEDURE_RUN_PLAN_UI_CLEAR_PROCEDURE_RUN_PLAN_SELECTION',
  RESET_STUDY_STATE = 'PROCEDURE_RUN_PLAN_UI_RESET_STUDY_STATE',
}

export interface SetSelectedProcedureRunAction {
  type: ProcedureRunPlanUIActionType.SET_SELECTED_PROCEDURE_RUN;
  studyId: string;
  procedureRun: ProcedureRunOrPlanStateSummary;
}

export function setSelectedProcedureRun(
  studyId: string,
  procedureRun: ProcedureRunOrPlanStateSummary
): SetSelectedProcedureRunAction {
  return {
    type: ProcedureRunPlanUIActionType.SET_SELECTED_PROCEDURE_RUN,
    studyId,
    procedureRun,
  };
}

export interface SetSelectedProcedureRunPlanAction {
  type: ProcedureRunPlanUIActionType.SET_SELECTED_PROCEDURE_RUN_PLAN;
  studyId: string;
  procedureRunPlan: ProcedureRunOrPlanStateSummary;
}

export function setSelectedProcedureRunPlan(
  studyId: string,
  procedureRunPlan: ProcedureRunOrPlanStateSummary
): SetSelectedProcedureRunPlanAction {
  return {
    type: ProcedureRunPlanUIActionType.SET_SELECTED_PROCEDURE_RUN_PLAN,
    studyId,
    procedureRunPlan,
  };
}

export interface SetCurrentProcedureRunTabAction {
  type: ProcedureRunPlanUIActionType.SET_CURRENT_PROCEDURE_RUN_TAB;
  studyId: string;
  tab: ProcedureRunTab;
}

export function setCurrentProcedureRunTab(
  studyId: string,
  tab: ProcedureRunTab
): SetCurrentProcedureRunTabAction {
  return {
    type: ProcedureRunPlanUIActionType.SET_CURRENT_PROCEDURE_RUN_TAB,
    studyId,
    tab,
  };
}

export interface AddPromotingPlanIdAction {
  type: ProcedureRunPlanUIActionType.ADD_PROMOTING_PLAN_ID;
  studyId: string;
  planId: string;
}

export function addPromotingPlanId(studyId: string, planId: string): AddPromotingPlanIdAction {
  return {
    type: ProcedureRunPlanUIActionType.ADD_PROMOTING_PLAN_ID,
    studyId,
    planId,
  };
}

export interface RemovePromotingPlanIdAction {
  type: ProcedureRunPlanUIActionType.REMOVE_PROMOTING_PLAN_ID;
  studyId: string;
  planId: string;
}

export function removePromotingPlanId(studyId: string, planId: string): RemovePromotingPlanIdAction {
  return {
    type: ProcedureRunPlanUIActionType.REMOVE_PROMOTING_PLAN_ID,
    studyId,
    planId,
  };
}

export interface SetProcedureRunPlanDesignStateAction {
  type: ProcedureRunPlanUIActionType.SET_PROCEDURE_RUN_PLAN_DESIGN_STATE;
  studyId: string;
  procedureRunPlanId: string;
  designState: ProcedureRunPlanDesignStateSummary;
}

export function setProcedureRunPlanDesignState(
  studyId: string,
  procedureRunPlanId: string,
  designState: ProcedureRunPlanDesignStateSummary
): SetProcedureRunPlanDesignStateAction {
  return {
    type: ProcedureRunPlanUIActionType.SET_PROCEDURE_RUN_PLAN_DESIGN_STATE,
    studyId,
    procedureRunPlanId,
    designState,
  };
}

export interface SetIsInEditModeAction {
  type: ProcedureRunPlanUIActionType.SET_IS_IN_EDIT_MODE;
  studyId: string;
  isInEditMode: boolean;
}

export function setIsInEditMode(studyId: string, isInEditMode: boolean): SetIsInEditModeAction {
  return {
    type: ProcedureRunPlanUIActionType.SET_IS_IN_EDIT_MODE,
    studyId,
    isInEditMode,
  };
}

export interface ClearProcedureRunPlanSelectionAction {
  type: ProcedureRunPlanUIActionType.CLEAR_PROCEDURE_RUN_PLAN_SELECTION;
  studyId: string;
}

export function clearProcedureRunPlanSelection(studyId: string): ClearProcedureRunPlanSelectionAction {
  return {
    type: ProcedureRunPlanUIActionType.CLEAR_PROCEDURE_RUN_PLAN_SELECTION,
    studyId,
  };
}

export interface ResetStudyStateAction {
  type: ProcedureRunPlanUIActionType.RESET_STUDY_STATE;
  studyId: string;
}

export function resetStudyState(studyId: string): ResetStudyStateAction {
  return {
    type: ProcedureRunPlanUIActionType.RESET_STUDY_STATE,
    studyId,
  };
}

export type ProcedureRunPlanUIAction =
  | SetSelectedProcedureRunAction
  | SetSelectedProcedureRunPlanAction
  | SetCurrentProcedureRunTabAction
  | AddPromotingPlanIdAction
  | RemovePromotingPlanIdAction
  | SetProcedureRunPlanDesignStateAction
  | SetIsInEditModeAction
  | ClearProcedureRunPlanSelectionAction
  | ResetStudyStateAction;
